import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Bell, ChevronDown } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { clearShopSelection } from '@/features/auth/authSlice';
import { clearCart } from '@/features/cart/cartSlice';
import { Avatar, CountBubble, IconButton } from '@/components/ui';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useUnreadNotifications } from '@/features/notifications/useUnreadNotifications';

export interface StoreHeaderProps {
    /** Extra controls between the store switcher and the bell (lg search pill etc.) */
    children?: React.ReactNode;
    /** Hide the bell + avatar cluster (when the page has its own right slot) */
    hideActions?: boolean;
    className?: string;
}

/**
 * M2 store switcher: shop name + connectivity + cashier. Tapping the name switches shop
 * (cart is cleared first — keep that order). Rendered by every T2 tab page, not by Layout.
 */
export const StoreHeader: React.FC<StoreHeaderProps> = ({ children, hideActions = false, className }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user, selectedShop } = useAppSelector((s) => s.auth);
    const network = useNetworkStatus();
    const unread = useUnreadNotifications();

    const handleSwitchShop = () => {
        dispatch(clearCart());
        dispatch(clearShopSelection());
        navigate('/shop-select');
    };

    return (
        <div className={clsx('flex items-center gap-2 lg:gap-3', className)}>
            <button type="button" onClick={handleSwitchShop} className="min-w-0 shrink text-left lg:mr-2" aria-label={t('auth.selectShop')}>
                <span className="flex items-center gap-1 text-[19px] lg:text-[22px] font-black leading-tight tracking-[-0.01em] text-ink-brand">
                    <span className="truncate">{selectedShop?.shopName}</span>
                    <ChevronDown className="h-[18px] w-[18px] shrink-0" />
                </span>
                <span className="flex items-center gap-1.5 text-xs font-bold text-ink-muted">
                    <span
                        className={clsx(
                            'h-[7px] w-[7px] rounded-full',
                            network === 'GOOD' && 'bg-status-success',
                            network === 'SLOW' && 'bg-status-warning',
                            network === 'OFFLINE' && 'bg-status-danger animate-pulse',
                        )}
                    />
                    <span className="truncate">
                        {t(`network.${network.toLowerCase()}` as 'network.good')} · {t('header.cashier')} {user?.fullname?.split(' ')[0]}
                    </span>
                </span>
            </button>
            {children}
            {!hideActions && (
                <>
                    <div className="flex-1 lg:hidden" />
                    <IconButton variant="card" size="xs" label={t('notifications.title')} onClick={() => navigate('/notifications')}>
                        <Bell />
                        <CountBubble count={unread} tone="danger" className="absolute -top-0.5 -right-0.5" />
                    </IconButton>
                    <button type="button" onClick={() => navigate('/attendance')} aria-label={t('attendance.title')} className="rounded-full active:scale-[.94]">
                        <Avatar name={user?.fullname} size="sm" status={network === 'OFFLINE' ? 'off' : 'online'} />
                    </button>
                </>
            )}
        </div>
    );
};
export default StoreHeader;
